import { useLocation, useNavigate } from 'react-router-dom';
import { FaTruckMonster, FaUsers } from 'react-icons/fa';
import { GoGear, GoHome } from 'react-icons/go';
import { BiBookOpen, BiTask } from 'react-icons/bi';
import { AiOutlineTeam, AiOutlineUser } from 'react-icons/ai';
import { BiCommentError } from "react-icons/bi";
import { GrTest } from "react-icons/gr";
import { GoPlug } from "react-icons/go";
import { GoKey } from "react-icons/go";
import { BsPen } from "react-icons/bs";
import { useTokensContext } from '../providers/TokensContextProvider';
import { useCallback } from 'react';
import { useBreadCrumbsContext } from '../providers/BreadCrumbsContextProvider';

// ---------------------------   Static   --------------------------------------- //
const NavPages = {
  // Single Help hub (Overview · Quickstart · SDK). /about kept for old links (not in sidebar).
  DOCS:               { path: '/docs',            auth:false, title: 'Help',            icon: BiBookOpen,     isSideBar:true,   isSub:false, isBeta: false, note: 'Help: overview, quickstart, Java SDK'},
  ABOUT:              { path: '/about',           auth:false, title: 'Help',            icon: BiBookOpen,     isSideBar:false,  isSub:false, isBeta: false, note: 'Alias → Help overview'},
  SIGNUP:             { path: '/signup',          auth:false, title: 'Sign Up',         icon: BsPen,          isSideBar:true,   isSub:false, isBeta: false, note: 'Get all the Kiponos goodness right here :)'},
  LOGIN:              { path: '/login',           auth:false, title: 'Sign In',         icon: GoKey,          isSideBar:true,   isSub:false, isBeta: false, note: 'Please sign in with your username and password'},
  CONFIGS_SHOW_SDK:   { path: '/configs/SDK',     auth:true,  title: 'Connect Your App',icon: GoPlug,         isSideBar:true,   isSub:false, isBeta: false, note: 'Your SDK tokens and JVM param for your current environment'},
  CONFIGS_HIDE_SDK:   { path: '/configs/nosdk',   auth:true,  title: 'Configs',         icon: GoGear,         isSideBar:true,   isSub:false, isBeta: false, note: 'Change your servers and apps configurations here online, with instant effect in realtime while your apps keep running.'},
  AVODA:              { path: '/avoda',           auth:true,  title: 'Task Mgr',        icon: BiTask,         isSideBar:true,   isSub:false, isBeta: true,  note: 'Developer First! TODO center. Brings order to the Dev tasks chaos using 3D friendly scenery.'},
  INVITE_USER:        { path: '/invite-user',     auth:true,  title: 'Invite Users',    icon: FaUsers,        isSideBar:true,   isSub:false, isBeta: false, note: 'Invite users to join your Team'},
  MY_PROFILE:         { path: '/my-profile',      auth:true,  title: 'My Profile',      icon: AiOutlineUser,  isSideBar:true,   isSub:false, isBeta: false, note: 'Update your contact details, change your email'},
  MONSTER:            { path: '/monster',         auth:false, title: 'Monster Road',    icon: FaTruckMonster, isSideBar:false,  isSub:false, isBeta: false, note: 'Play Time!'},
  TEAM_FUN:           { path: '/teamfun',         auth:true,  title: 'Snooker',         icon: AiOutlineTeam,  isSideBar:false,  isSub:false, isBeta: false, note: 'Developer break time - free your mind!'},
  PAGE_NOT_FOUND:     { path: '/page-not-found',  auth:false, title: 'Page Not Found',  icon: BiCommentError, isSideBar:false,  isSub:false, isBeta: false, note: 'Sorry, not a Kiponos page.'},
  LOGOUT:             { path: '/logout',          auth:true,  title: 'Logout',          icon: GoHome,         isSideBar:false,  isSub:false, isBeta: false, note: 'Logout'},
  TESTORAMA:          { path: '/testorama',       auth:false, title: 'TestoRama',       icon: GrTest,         isSideBar:false,  isSub:false, isBeta: false, note: 'TestoRama Lab'},

  // DOIT:               { path: '/doit',            auth:true,  title: 'Do It!',        icon: BiTask,         isSideBar:true,   isSub:false, note: 'Developer First! TODO center. Brings order to the Dev tasks chaos using 3D friendly scenery.'},
  //TRIPTIDY:           { path: '/triptidy',        auth:false, title: 'TripTidy',      icon: FaTruckMonster, isSideBar:true,   isSub:false, note: 'TripTidy - Activity Planner'},
}

const PageKeys = Object.keys(NavPages).reduce( (pageKeys, pageKey) => ({...pageKeys, [pageKey]: pageKey }) , {});
const DefaultPageKey = PageKeys.CONFIGS_HIDE_SDK;

export const getPage = (pageKey) => {
  if (!pageKey) {
    console.error('[useKiponosNavigator getPage] Got Empty pageKey!');
  }

  const page = NavPages[pageKey];

  if (!page) {
    console.error('[useKiponosNavigator getPage] No Page for [key: %s]', pageKey);
  }

  return page;
}

export const isKnownPath = (aPath) => {
  if (!aPath) {
    return false;
  }
  return Object.keys(PageKeys).map(pageKey => getPage(pageKey)).some(page => page.path === aPath);
}

/*
  --------------------------------------------------   useKiponosNavigator   -------------------------------------

  Navigating to a routed page:
    - Highlight the SideBar Item.
    - Display Title on the Control Bar.
    - nav( pagePath )
    - 
*/
export default function useKiponosNavigator() {
  const {accessToken}           = useTokensContext();
  const {pathname:browserPath}  = useLocation();
  const {setControlBar}         = useBreadCrumbsContext();
  const nav                     = useNavigate();
  
  // ---  Is Browser Path Matches  ---
  //
  const isBrowserPathMatches = useCallback((aPath) => {
    //console.log('isBrowserPathMatches [aPath: %s] [browserPath: %s]', aPath, browserPath);
    return browserPath?.startsWith(aPath);
  }, [browserPath]);


  //--- Is PageKey Active
  //
  const getIsPageKeyActive = useCallback((pageKey) => {
    return isBrowserPathMatches(NavPages[pageKey].path);
  }, [isBrowserPathMatches]);
  
  const isPageKeyActive = useCallback((pageKey) => getIsPageKeyActive(pageKey), [getIsPageKeyActive]);

  // ---  Get Style  ---
  //
  const getStyle = useCallback((pageKey) => {
    return isPageKeyActive(pageKey) ? 'sideBarLinkActive': 'sideBarLink';
  }, [isPageKeyActive]);

  // 
  // ---[  Navigate To  ]---
  // 
  const navTo = useCallback((pageKey, force = false) => {
    //console.log('[useKiponosNavigator navTo] [pageKey: %s]', pageKey);
    
    // Already on target?
    if (isPageKeyActive(pageKey) && !force) {
      //console.debug('[useKiponosNavigator navTo] Already on target page.');
      return;      
    }

    // pageKey not found.
    if (!PageKeys[pageKey]) {
      console.error('[useKiponosNavigator navTo] Page Not Found [pageKey: %s]', pageKey);
      throw new Error('[useKiponosNavigator navTo] - Page Not Found [pageKey: '+pageKey+']');
    }
    
    const page = NavPages[pageKey];
    
    // Page data not found
    if (!page) {
      console.error('[useKiponosNavigator navTo] Page Data Not Found [pageKey: %s]', pageKey);
      throw new Error('No SideBar Page for pageKey: '+pageKey);
    }    
    

    // Auth Check
    if ( Boolean(page.auth) && !Boolean(accessToken)) {
      //console.error('[useKiponosNavigator navTo] Unauthorized navigation. Ignoring.  [To pageKey: %s]', pageKey);
      return;
    }

    
    // ControlBar will get the page data to display.
    // SideBar's NavLink will highlight the active one.
    setControlBar({
      pageKey: pageKey
    });
    
    // NAVIGATE TO 
    //console.log('[useKiponosNavigator navTo] %s', page.title);

    // Remember the current active page before navigating to a new one.
    // const fromPage = activePageKey ? activePageKey : DefaultPageKey;
    const fromPage = () =>  {
      const activePageKey = Object.keys(PageKeys).find(pageKey => isPageKeyActive(pageKey));
      return activePageKey ? activePageKey : DefaultPageKey;
    }
    

    // Allowed Navigation    
    nav(page.path, { state: { from: fromPage() } });
  }, [accessToken, isPageKeyActive, nav, setControlBar]);

  const navToDefaultPage = useCallback(() => {
    navTo(DefaultPageKey);
  }, [navTo]);

  // ------  Return  ------

  return {
    PageKeys,
    DefaultPageKey,
    navTo,
    navToDefaultPage,
    getStyle    
  }
}
